import { Injectable } from '@angular/core';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { Store } from '@ngxs/store';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { ITrainingEventResponse } from '@training/models/interfaces/training-event-response.interface';
import { TrainingEvent } from '@training/models/training/training-event.model';
import {
  RememberTrainingEvent,
  RememberTrainingEvents,
  ResetTrainingEvents,
  UpdatePinnedEvents
} from '@training/states/training-events/training-events-state.actions';
import {
  TrainingEventsState,
  TrainingEventsStateModel
} from '@training/states/training-events/training-events.state';
import { cloneDeep } from 'lodash';
import { map, Observable, of, switchMap, take, tap } from 'rxjs';

import { TrainingEventsDataService } from './training-events-data.service';

@Injectable({
  providedIn: 'root'
})
export class TrainingEventsService {
  public trainingEventsState$: Observable<TrainingEventsStateModel>;
  public trainingEvents$: Observable<TrainingEvent[]>;

  constructor(
    private trainingEventsDataService: TrainingEventsDataService,
    private store: Store,
    private appSettingsService: AppSettingsService
  ) {
    this.trainingEventsState$ = this.store.select(TrainingEventsState.getState);
    this.trainingEvents$ = this.store.select(
      TrainingEventsState.getTrainingEvents
    );
  }

  @Dispatch()
  public getTrainingEvents(): Observable<
    RememberTrainingEvents | Array<TrainingEvent>
    > {
    if (
      !this.appSettingsService.configuration.store.trainingEventsStateEnabled ||
      !this.store.selectSnapshot(TrainingEventsState.getState)
        .isLoadedSuccessfully
    ) {
      return this.trainingEventsDataService.getTrainingEvents().pipe(
        map((trainingEventResponse: ITrainingEventResponse) => {
          const trainingEvents = trainingEventResponse.trainingEvents.map(
            (trainingEvent) =>
              new TrainingEvent(
                trainingEvent,
                trainingEventResponse.pinnedTrainingEvents
              )
          );
          return new RememberTrainingEvents(
            trainingEvents,
            true,
            trainingEventResponse.isSMSNotificationSettingEnabled,
            trainingEventResponse.isProfileSettingEnabled
          );
        })
      );
    }
    return this.trainingEvents$;
  }

  public getTrainingEventByReservationId(
    geminiReservationId: string
  ): Observable<TrainingEvent> {
    if (
      !this.appSettingsService.configuration.store.trainingEventsStateEnabled
    ) {
      return this.getTrainingEvent(geminiReservationId, { updateState: false });
    }

    return this.trainingEventsState$.pipe(
      switchMap((trainingEventsStateModel: TrainingEventsStateModel) => {
        const trainingEvent = trainingEventsStateModel.trainingEvents?.find(
          (t: TrainingEvent) => t.geminiReservationId === geminiReservationId
        );

        if (!trainingEventsStateModel.isLoadedSuccessfully || !trainingEvent) {
          return this.getTrainingEvent(geminiReservationId, {
            updateState: true
          });
        }

        return of(trainingEvent);
      })
    );
  }

  public UpdatePinnedEvents(id: string, isPinned: boolean): void {
    this.BuildUpdatePinnedEventsObservable(id, isPinned).subscribe();
  }

  private BuildUpdatePinnedEventsObservable(
    id: string,
    isPinned: boolean
  ): Observable<string[]> {
    return this.trainingEvents$.pipe(
      take(1),
      map((trainingEvents: TrainingEvent[]) =>
        this.UpdatePinnedEvent(trainingEvents, id, isPinned)
      ),
      tap((trainingEvents: TrainingEvent[]) =>
        this.UpdatePinnedEventState(trainingEvents)
      ),
      switchMap((trainingEvents: TrainingEvent[]) =>
        this.CallUpdatePinnedEventsDataService(trainingEvents)
      )
    );
  }

  private UpdatePinnedEvent(
    trainingEvents: TrainingEvent[],
    id: string,
    isPinned: boolean
  ): TrainingEvent[] {
    const events = cloneDeep(trainingEvents);
    const index = events.findIndex((event) =>
      event.isFTO
        ? event.reservationId === id
        : event.geminiReservationId === id
    );
    events[index].isPinned = isPinned;
    return events;
  }

  private UpdatePinnedEventState(trainingEvents: TrainingEvent[]): void {
    this.store.dispatch(new UpdatePinnedEvents(trainingEvents));
  }

  private CallUpdatePinnedEventsDataService(
    trainingEvents: TrainingEvent[]
  ): Observable<string[]> {
    const events = trainingEvents
      .filter((event) => event.isPinned)
      .map((event) =>
        event.isFTO ? event.reservationId : event.geminiReservationId
      );

    return this.trainingEventsDataService.updatePinnedEvents(events);
  }

  private getTrainingEvent(
    geminiReservationId: string,
    options: { updateState: boolean } = { updateState: false }
  ): Observable<TrainingEvent> {
    return this.trainingEventsDataService
      .getTrainingEvent(geminiReservationId)
      .pipe(
        tap((trainingEvent) => {
          if (options.updateState) {
            this.store.dispatch(new RememberTrainingEvent(trainingEvent, true));
          }
        })
      );
  }

  @Dispatch()
  public resetTrainingEventsState(): ResetTrainingEvents {
    return new ResetTrainingEvents();
  }
}
